import React from "react";
import { useLang } from "../lib/i18n";
import { PageHeader } from "../lib/ui";
import { Button } from "../components/ui/button";
import { useInstall } from "../components/InstallPrompt";
import { Download, Monitor, Apple, Terminal, Cloud, HardDrive, Smartphone, CheckCircle2 } from "lucide-react";

const RELEASES = process.env.REACT_APP_DESKTOP_RELEASES_URL || "";
const VERSION = "1.0.0";

const builds = [
  { os: "Windows 10 / 11", icon: Monitor, file: `Stride-${VERSION}-win-x64.exe`, hint: "Installateur (.exe). Double-cliquez, puis suivez l'assistant." },
  { os: "macOS 12+", icon: Apple, file: `Stride-${VERSION}-mac-arm64.dmg`, hint: "Image disque (.dmg). Glissez Stride dans Applications." },
  { os: "Linux", icon: Terminal, file: `Stride-${VERSION}-linux-x86_64.AppImage`, hint: "AppImage (ou .deb pour Ubuntu/Debian). Rendez le fichier exécutable." },
];

export default function DownloadApp() {
  const { t } = useLang();
  const { canInstall, install, installed } = useInstall();

  return (
    <div data-testid="download-page">
      <PageHeader eyebrow={t("Installation")} title={t("Télécharger Stride")}
        subtitle={t("Installez Stride sur votre ordinateur ou votre téléphone pour travailler même sans réseau.")} testid="download-header" />

      <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid="download-pwa">
        <div className="flex items-start gap-3">
          <Smartphone className="h-5 w-5 text-[#F05A28] mt-0.5" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold text-slate-900">{t("Installation immédiate depuis le navigateur (recommandée)")}</h2>
            <p className="text-xs text-slate-500 mt-1">{t("Sur Chrome ou Edge, Stride s'installe en un clic : icône sur le bureau ou l'écran d'accueil, fenêtre dédiée, saisie et génération des rapports hors ligne, mises à jour automatiques.")}</p>
            {installed ? (
              <p className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="download-installed"><CheckCircle2 className="h-4 w-4" />{t("Stride est déjà installé sur cet appareil.")}</p>
            ) : canInstall ? (
              <Button onClick={install} className="mt-3 h-10 rounded-md bg-[#F05A28] hover:bg-[#D84A1B]" data-testid="download-pwa-btn">
                <Download className="h-4 w-4 mr-1.5" />{t("Installer Stride maintenant")}
              </Button>
            ) : (
              <p className="mt-3 text-xs text-slate-600" data-testid="download-pwa-hint">{t("Chrome / Edge : icône « Installer » à droite de la barre d'adresse, ou menu ⋮ › « Installer Stride ». iPhone : Partager › « Sur l'écran d'accueil ».")}</p>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm p-5 mb-4" data-testid="download-desktop">
        <h2 className="text-sm font-semibold text-slate-900 mb-1">{t("Logiciel de bureau Stride")} <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-100 text-slate-600">v{VERSION}</span></h2>
        <p className="text-xs text-slate-500 mb-4">{t("Au premier lancement, choisissez le mode de fonctionnement du poste :")}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="rounded-md border border-slate-200 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800"><Cloud className="h-4 w-4 text-[#0284C7]" />{t("Connecté au serveur")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("Données partagées avec les superviseurs et l'équipe SI. Fonctionne hors ligne et se synchronise au retour du réseau.")}</p>
          </div>
          <div className="rounded-md border border-slate-200 p-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-800"><HardDrive className="h-4 w-4 text-[#F05A28]" />{t("Autonome (100 % local)")}</p>
            <p className="text-xs text-slate-500 mt-1">{t("Base de données et serveur embarqués sur l'ordinateur. Aucune connexion nécessaire ; les données restent sur ce poste.")}</p>
          </div>
        </div>
        <div className="divide-y divide-slate-100 rounded-md border border-slate-200">
          {builds.map((b) => (
            <div key={b.os} className="flex flex-wrap items-center gap-3 p-3" data-testid={`download-build-${b.os.split(" ")[0].toLowerCase()}`}>
              <b.icon className="h-4 w-4 text-slate-500" />
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-medium text-slate-800">{b.os}</p>
                <p className="text-xs text-slate-500">{t(b.hint)}</p>
              </div>
              <code className="text-[11px] text-slate-500 font-mono">{b.file}</code>
              {RELEASES ? (
                <a href={`${RELEASES.replace(/\/$/, "")}/${b.file}`} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-[#0F172A] text-white text-xs font-semibold hover:bg-[#1E293B]">
                  <Download className="h-3.5 w-3.5" />{t("Télécharger")}
                </a>
              ) : (
                <span className="text-[11px] italic text-slate-400" data-testid="download-pending">{t("Fourni par votre administrateur")}</span>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-4">{t("Identifiants du mode autonome définis à l'installation (par défaut admin@stride.local / Admin@Stride2026). Les autres comptes se créent ensuite dans Administration.")}</p>
      </section>
    </div>
  );
}
